import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaUsers, FaBook, FaClock, FaSpinner, FaSearch, FaTimes } from 'react-icons/fa';
import { notifyError } from '../../utils/notifications';
import { useAuth } from '../../context/AuthContext';
import { fetchMyStudents } from '../../services/courseService';
import { formatDuration } from '../../lib/time';
import usePagination from '../../hooks/usePagination';
import SEO from '../../components/common/SEO';
import Pagination from '../../components/common/Pagination';
import AdminEmptyState from '../Admin/components/AdminEmptyState';
import { AdminStat } from '../Admin/components/AdminListCard';
import DashboardShell from './DashboardShell';

const PAGE_SIZE = 9;

const initials = (name = '') => (name.trim().charAt(0).toUpperCase() || '?');

const StudentCard = ({ s, showCourse }) => {
  const total = Number(s.available_lessons) || 0;
  const done = Number(s.completed_lessons) || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <article className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-10 h-10 rounded-full bg-designer-primary/10 text-designer-primary flex items-center justify-center text-sm font-bold shrink-0" aria-hidden="true">
          {initials(s.name)}
        </span>
        <div className="min-w-0">
          <h3 className="font-semibold text-designer-dark break-words leading-snug">{s.name}</h3>
          <p className="text-xs text-gray-400 break-all leading-snug">{s.email}</p>
        </div>
      </div>
      {showCourse && (
        <p className="text-xs text-gray-500 break-words">
          Enrolled in <Link to={`/admin/studio/${s.course_slug || s.course_id}`} className="font-semibold text-designer-primary hover:underline">{s.course_title}</Link>
        </p>
      )}
      <div>
        <div className="flex items-center justify-between gap-3 text-xs text-gray-500 mb-1">
          <span className="tabular-nums">{done}/{total} classes</span>
          <span className="font-semibold text-designer-primary tabular-nums">{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${s.name} progress`}>
          <div className="h-full bg-gradient-to-r from-designer-primary to-red-500 rounded-full" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100 mt-auto text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5 tabular-nums">
          <FaClock className="text-designer-primary w-3 h-3" />
          {formatDuration(Number(s.watched_seconds) || 0) || '0m'} watched
        </span>
        <span className="text-gray-400">
          {s.enrolled_at ? new Date(s.enrolled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '·'}
        </span>
      </div>
    </article>
  );
};

const TeacherStudents = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isTeacher, isAuthenticated, getAuthHeaders } = useAuth();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [courseId, setCourseId] = useState(searchParams.get('course') || 'all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/dashboard/teacher/students' } });
      return;
    }
    if (!isTeacher) {
      notifyError('Access denied. Teachers only.');
      navigate('/');
    }
  }, [isAuthenticated, isTeacher, navigate]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setRows(await fetchMyStudents(getAuthHeaders()));
    } catch (err) {
      notifyError(err.message || 'Failed to load students');
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (isTeacher) load();
  }, [isTeacher, load]);

  const courses = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.course_id)) map.set(r.course_id, { id: r.course_id, title: r.course_title });
    });
    return [...map.values()];
  }, [rows]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => (
    rows.filter((r) =>
      (courseId === 'all' || String(r.course_id) === String(courseId)) &&
      (!q || `${r.name} ${r.email}`.toLowerCase().includes(q))
    )
  ), [rows, courseId, q]);
  const pager = usePagination(filtered, PAGE_SIZE, { resetKey: `${courseId}|${q}` });

  const uniqueStudents = useMemo(() => new Set(rows.map((r) => r.id)).size, [rows]);
  const totalWatched = rows.reduce((n, r) => n + (Number(r.watched_seconds) || 0), 0);

  if (!isTeacher) return null;

  return (
    <DashboardShell
      role="teacher"
      title={`Students${user?.name ? ` · ${user.name}` : ''}`}
      subtitle="Everyone enrolled in your courses"
      headerActions={(
        <Link
          to="/account/password"
          className="inline-flex items-center px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:text-designer-primary hover:border-designer-primary/40 transition-colors"
        >
          Change password
        </Link>
      )}
    >
      <SEO title="My Students | Arvix Academy" description="Students enrolled in your courses" />
      <div className="space-y-6 animate-fadeIn">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <AdminStat icon={FaUsers} label="Students" value={uniqueStudents} sub={`across ${courses.length} course${courses.length === 1 ? '' : 's'}`} tile="bg-blue-50 text-blue-600" />
          <AdminStat icon={FaBook} label="Enrollments" value={rows.length} sub="course signups" tile="bg-red-50 text-designer-primary" />
          <AdminStat icon={FaClock} label="Watch Time" value={formatDuration(totalWatched) || '0m'} sub="by your students" tile="bg-green-50 text-green-600" />
        </div>

        <div className="flex flex-col gap-3">
          <div className="relative">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email…"
              aria-label="Search students"
              className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-designer-primary outline-none text-sm shadow-sm"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                <FaTimes className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {courses.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" aria-label="Filter by course">
              <button
                type="button"
                role="tab"
                aria-selected={courseId === 'all'}
                onClick={() => setCourseId('all')}
                className={`shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
                  courseId === 'all'
                    ? 'bg-designer-primary text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-designer-primary/40 hover:text-designer-primary'
                }`}
              >
                All courses ({rows.length})
              </button>
              {courses.map((c) => {
                const active = String(courseId) === String(c.id);
                const count = rows.filter((r) => r.course_id === c.id).length;
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setCourseId(String(c.id))}
                    title={c.title}
                    className={`shrink-0 max-w-56 truncate px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
                      active
                        ? 'bg-designer-primary text-white shadow-sm'
                        : 'bg-white border border-gray-200 text-gray-600 hover:border-designer-primary/40 hover:text-designer-primary'
                    }`}
                  >
                    {c.title} ({count})
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <FaSpinner className="animate-spin text-designer-primary w-8 h-8" />
          </div>
        ) : filtered.length === 0 ? (
          <AdminEmptyState
            icon={FaUsers}
            title={rows.length === 0 ? 'No students yet' : 'No matching students'}
            description={rows.length === 0 ? 'Students will appear here once they enroll in your courses.' : 'Try a different search or course.'}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" ref={pager.listRef}>
              {pager.items.map((s) => (
                <StudentCard key={`${s.course_id}-${s.id}`} s={s} showCourse={courseId === 'all'} />
              ))}
            </div>
            <Pagination currentPage={pager.page} totalPages={pager.totalPages} onPageChange={pager.changePage} />
          </>
        )}
      </div>
    </DashboardShell>
  );
};

export default TeacherStudents;
